// File: frontend/src/components/marketing/RouterNetworkSection.js
//
// "Your hotspots. One connected platform." — the network diagram beside the
// details of whichever location is selected in it.
//
// ── THE SELECTION IS REAL ───────────────────────────────────────────────────
//
// This section used to say "Selected on the map" beside a fixed card while the
// diagram's `selectedId` was a hard-coded default prop and its nodes had no
// handlers: nothing was selectable, and the label promised an interaction that
// did not exist. The selection is state here now, the diagram is told what is
// selected and reports back when a location is chosen, and the card is built
// from that same location — so the two cannot disagree.
//
// ── WHAT THE HEADING MAY CLAIM ──────────────────────────────────────────────
//
// It used to read "Every Router. One Connected Platform.". PesaFii talks to
// MikroTik routers over the RouterOS REST API and there is no adapter for any
// other vendor, so "every router" was not true, and it contradicted the page's
// own how-it-works step ("a supported MikroTik hotspot router"). This says what
// the product does: your hotspots, on one platform.
//
// It is also not a map. The positions are a fixed ring around the hub, and none
// of them means a place on the ground, so the copy says diagram.
//
// Every figure the card shows — active users, uptime, CPU, memory, data used —
// is something PesaFii genuinely samples from a router. Fictional values, real
// fields; no API calls.

import React, { useState } from "react";
import { Box, Container, Typography, alpha } from "@mui/material";
import NetworkTopologyVisual from "./visuals/NetworkTopologyVisual";
import MarketingRouterVisual from "./visuals/MarketingRouterVisual";
import { useReveal } from "./landingMotion";
import { DEMO_LOCATIONS, DEMO_DEFAULT_LOCATION_ID } from "../../data/marketingDemoData";

export default function RouterNetworkSection() {
  const root = useReveal();
  const [selectedId, setSelectedId] = useState(DEMO_DEFAULT_LOCATION_ID);
  const selected = DEMO_LOCATIONS.find((l) => l.id === selectedId) || DEMO_LOCATIONS[0];

  return (
    <Box
      component="section"
      ref={root}
      aria-labelledby="network-title"
      sx={(t) => ({
        // Was py 6/10 with a 3/5 gap under the heading, which left a lot of
        // empty space above the thing the section exists to show.
        py: { xs: 5, md: 8 },
        position: "relative",
        overflow: "hidden",
        bgcolor: "action.hover",
        backgroundImage: `radial-gradient(ellipse 55% 60% at 35% 55%, ${alpha(t.palette.primary.main, t.palette.mode === "dark" ? 0.1 : 0.07)}, transparent 70%)`,
      })}
    >
      <Container maxWidth="lg" sx={{ minWidth: 0 }}>
        <Box sx={{ textAlign: "center", maxWidth: 720, mx: "auto", mb: { xs: 2.5, md: 3.5 } }}>
          <Typography data-reveal="" sx={{ fontSize: 13, fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase", color: "primary.main", mb: 1 }}>
            Your locations
          </Typography>
          <Typography data-reveal="" id="network-title" component="h2" sx={{ fontSize: "clamp(1.6rem, 3.5vw, 2.4rem)", lineHeight: 1.18, fontWeight: 800, letterSpacing: "-0.02em", color: "text.primary" }}>
            Your hotspots. One connected platform.
          </Typography>
          <Typography data-reveal="" sx={{ mt: 1.5, fontSize: { xs: 15, md: 16.5 }, color: "text.secondary", lineHeight: 1.6 }}>
            Every hotspot location reports to one place. Choose a location to see whether it is
            online, how many people are connected, and how the router is holding up.
          </Typography>
        </Box>

        {/* The diagram comes first in the DOM, so on a phone it sits above the
            details it controls. */}
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1.4fr 1fr" }, gap: { xs: 2.5, md: 5 }, alignItems: "center" }}>
          <Box sx={{ minWidth: 0 }}>
            {/* Full diagram from sm up; the three strongest locations on phones. */}
            <Box sx={{ display: { xs: "none", sm: "block" } }}>
              <NetworkTopologyVisual height={460} selectedId={selectedId} onSelect={setSelectedId} />
            </Box>
            <Box sx={{ display: { xs: "block", sm: "none" } }}>
              <NetworkTopologyVisual height={320} compact selectedId={selectedId} onSelect={setSelectedId} />
            </Box>
            <Typography sx={{ mt: 1, textAlign: "center", fontSize: 11.5, color: "text.disabled" }}>
              Choose a location to see its details
            </Typography>
          </Box>

          <Box data-reveal="" sx={{ minWidth: 0 }}>
            <Typography sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.25, fontSize: 12.5, fontWeight: 700, color: "text.secondary" }}>
              <Box component="span" aria-hidden sx={{ width: 10, height: 10, borderRadius: "50%", border: "2px solid", borderColor: "primary.main" }} />
              Selected location
            </Typography>
            {/* The card is rebuilt for the chosen location, and announced, so a
                screen-reader user hears the result of their choice. */}
            <Box aria-live="polite">
              <MarketingRouterVisual location={selected} />
            </Box>
          </Box>
        </Box>
      </Container>
    </Box>
  );
}
